import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Select } from "@/components/ui/Select";
import { apiFetch } from "@/lib/api";
import type { Page, PropertyListItemResource, UnitResource } from "@/lib/api-types";
import { labelFor } from "@/lib/field-labels";

/**
 * The Property -> Unit cascading picker (Story 15.9, judgement call 4) —
 * extracted out of `AddLeaseDialog.tsx` (Story 15.3) once `AddTenantDialog.tsx`
 * needed the identical cascade a second time: same `GET /properties` +
 * `GET /properties/{id}/units` queries, same disabled-until-a-property-is-
 * chosen behavior, same sentinel-unit exclusion inherited for free from the
 * units route itself (AD-18) — this component fetches nothing of its own
 * invention, it only renders exactly what those two routes return.
 *
 * Chosen over duplicating the block a second time (the spec's own explicitly
 * offered alternative) because the logic is genuinely identical between the
 * two callers, not just superficially similar — same query keys shape, same
 * cascade-reset-on-property-change rule, same disabled/placeholder copy —
 * and `AddLeaseDialog.tsx` itself already establishes this codebase's
 * "shared components over per-page one-offs" discipline elsewhere
 * (`KeyFactsStrip`, `RecordPage`). `usePropertyUnitCascade` owns the
 * network/state half so each dialog's own submit handler can still read
 * `propertyId`/`unitId` directly; `PropertyUnitPicker` owns only the two
 * `<Select>` elements, so each dialog keeps control of surrounding layout
 * (`autoFocus` differs: `AddLeaseDialog.tsx` puts it here, `AddTenantDialog.tsx`
 * puts it on its own first field instead).
 */
export function usePropertyUnitCascade(open: boolean) {
  const [propertyId, setPropertyId] = useState("");
  const [unitId, setUnitId] = useState("");

  const propertiesQuery = useQuery({
    queryKey: ["properties", "list", "for-property-unit-picker"] as const,
    queryFn: () => apiFetch<Page<PropertyListItemResource>>("/properties"),
    enabled: open,
  });

  //: Cascading — disabled/empty until a property is chosen. Never returns
  //: the sentinel unit (AD-18, inherited from the route itself).
  const unitsQuery = useQuery({
    queryKey: ["properties", propertyId, "units", "for-property-unit-picker"] as const,
    queryFn: () => apiFetch<Page<UnitResource>>(`/properties/${propertyId}/units`),
    enabled: open && propertyId !== "",
  });

  function handlePropertyChange(value: string) {
    setPropertyId(value);
    // A unit chosen under the previous property is never a legal choice
    // under the new one — cleared alongside it rather than left silently
    // stale.
    setUnitId("");
  }

  function reset() {
    setPropertyId("");
    setUnitId("");
  }

  return {
    propertyId,
    unitId,
    setUnitId,
    handlePropertyChange,
    reset,
    properties: propertiesQuery.data?.data ?? [],
    units: unitsQuery.data?.data ?? [],
  };
}

export function PropertyUnitPicker({
  autoFocus,
  propertyId,
  unitId,
  properties,
  units,
  onPropertyChange,
  onUnitChange,
}: {
  autoFocus?: boolean;
  propertyId: string;
  unitId: string;
  properties: PropertyListItemResource[];
  units: UnitResource[];
  onPropertyChange: (value: string) => void;
  onUnitChange: (value: string) => void;
}) {
  return (
    <>
      <label className="flex flex-col gap-1">
        <span className="text-label font-medium text-ink-secondary">{labelFor("property_nickname")}</span>
        <Select
          autoFocus={autoFocus}
          value={propertyId}
          onChange={(event) => onPropertyChange(event.target.value)}
        >
          <option value="">Choose one…</option>
          {properties.map((property) => (
            <option key={property.id} value={property.id}>
              {property.nickname}
            </option>
          ))}
        </Select>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-label font-medium text-ink-secondary">{labelFor("unit_id")}</span>
        <Select value={unitId} onChange={(event) => onUnitChange(event.target.value)} disabled={propertyId === ""}>
          <option value="">{propertyId === "" ? "Choose a property first…" : "Choose one…"}</option>
          {units.map((unit) => (
            <option key={unit.id} value={unit.id}>
              {unit.label}
            </option>
          ))}
        </Select>
      </label>
    </>
  );
}
